import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import './MyFavorites.css';

function MyFavorites() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useLanguage();

  const { data: favorites, isLoading } = useQuery({
    queryKey: ['user-favorites'],
    queryFn: async () => {
      try {
        const res = await apiClient.get('/user/favorites');
        return res.data;
      } catch (error) {
        if (error.response?.status === 401) {
          return [];
        }
        throw error;
      }
    },
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: async (productId) => {
      return apiClient.delete(`/user/favorites/${productId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-favorites']);
      queryClient.invalidateQueries(['favorites-count']);
      notification.success(t('removedFromFavorites'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorRemovingFavorite'));
    },
  });

  if (isLoading) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (!favorites || favorites.length === 0) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="favorites-header">
            <h1>{t('favoritesTitle')}</h1>
            <p className="favorites-subtitle">{t('favoritesSubtitle')}</p>
          </div>
          <div className="empty-favorites">
            <div className="empty-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </div>
            <h2>{t('emptyFavoritesText')}</h2>
            <p>{t('emptyFavoritesHint')}</p>
            <Link to="/products" className="empty-state-primary-btn">
              {t('viewProducts')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page">
      <div className="favorites-container">
        <div className="favorites-header">
          <h1>{t('favoritesTitle')}</h1>
          <p className="favorites-subtitle">
            {t('favoritesCount').replace('{n}', favorites.length)}
          </p>
        </div>

        <div className="favorites-grid">
          {favorites.map((favorite) => (
            <div key={favorite.id} className="favorite-card">
              <Link to={`/products/${favorite.slug}`} className="favorite-image">
                {favorite.primary_image ? (
                  <img src={favorite.primary_image} alt={favorite.name} />
                ) : (
                  <div className="image-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                      <path d="M9 9h6v6H9z"></path>
                    </svg>
                  </div>
                )}
                {favorite.compare_price && parseFloat(favorite.compare_price) > parseFloat(favorite.price) && (
                  <span className="sale-badge">{t('sale')}</span>
                )}
              </Link>
              <div className="favorite-info">
                <Link to={`/products/${favorite.slug}`}>
                  <h3 className="favorite-name">{favorite.name}</h3>
                </Link>
                <div className="favorite-price">
                  <span className="price">
                    {favorite.price ? parseFloat(favorite.price).toFixed(2) : '0.00'} €
                  </span>
                  {favorite.compare_price && parseFloat(favorite.compare_price) > parseFloat(favorite.price) && (
                    <span className="compare-price">
                      {parseFloat(favorite.compare_price).toFixed(2)} €
                    </span>
                  )}
                </div>
                <button
                  onClick={() => removeFavoriteMutation.mutate(favorite.product_id)}
                  className="remove-favorite-btn"
                  disabled={removeFavoriteMutation.isLoading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                  </svg>
                  {t('removeFromFavorites')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MyFavorites;
